import { z } from 'zod';
import {
  AnalysisBadgeSchema,
  AnalysisCostSummarySchema,
  AnalysisPercentageBreakdownSchema,
  AnalysisTweetsAnalyzedSchema,
  AnalysisUsageSummarySchema,
  AnalysisWeekdayWeekendSchema,
} from './shared/analysis.js';

const trimmedString = z.string().transform((value) => value.trim());
const nonEmptyString = trimmedString.refine((value) => value.length > 0, 'value cannot be empty');

export const ProfileAnalysisSummarySectionSchema = z
  .object({
    overview: nonEmptyString.optional(),
    personality: nonEmptyString.optional(),
    contentFocus: nonEmptyString.optional(),
    audienceInteraction: nonEmptyString.optional(),
  })
  .strict();

export const ProfileAnalysisTopicSchema = z
  .object({
    topic: nonEmptyString,
    representativeTweetId: nonEmptyString.optional(),
  })
  .strict();

export const ProfileAnalysisHighlightSchema = z
  .object({
    id: nonEmptyString,
    title: nonEmptyString,
    summary: nonEmptyString.optional(),
    tweetIds: z.array(nonEmptyString),
    reason: nonEmptyString.optional(),
    category: nonEmptyString.optional(),
  })
  .strict();

export const ProfileAnalysisBadgeSchema = AnalysisBadgeSchema.strict();

export const ProfileAnalysisActivityPatternsSchema = z
  .object({
    mostActiveTime: nonEmptyString,
    tweetFrequency: nonEmptyString,
    engagementRate: z.number(),
  })
  .strict();

export const ProfileAnalysisPostingTimeBucketSchema = AnalysisPercentageBreakdownSchema.strict();

export const ProfileAnalysisPostingTimePatternsSchema = z
  .object({
    summary: nonEmptyString.optional(),
    peakHours: z.array(ProfileAnalysisPostingTimeBucketSchema).optional(),
    activeDays: z.array(ProfileAnalysisPostingTimeBucketSchema).optional(),
    weekdayVsWeekend: AnalysisWeekdayWeekendSchema.optional(),
  })
  .strict();

const RangeSchema = z.object({
  min: nonEmptyString,
  max: nonEmptyString,
});

const ProfileAnalysisTweetsAnalyzedSchema = AnalysisTweetsAnalyzedSchema.extend({
  dateRange: RangeSchema.optional(),
  sampleTweetIds: z.array(z.string()).optional(),
}).strict();

const ProfileAnalysisAiUsageSchema = AnalysisUsageSummarySchema.strict();

const ProfileAnalysisAiCostSchema = AnalysisCostSummarySchema.strict();

export const ProfileAnalysisStructureSchema = z
  .object({
    summary: z.union([trimmedString, ProfileAnalysisSummarySectionSchema]).optional(),
    topics: z.array(ProfileAnalysisTopicSchema),
    highlights: z.array(ProfileAnalysisHighlightSchema),
    estimatedCharacterCount: z.number().optional(),
    aiMetadata: z
      .object({
        model: nonEmptyString,
        timestamp: z.string().optional(),
        generationTimeMs: z.number().optional(),
        tweetsAnalyzed: ProfileAnalysisTweetsAnalyzedSchema.optional(),
        usage: ProfileAnalysisAiUsageSchema.optional(),
        cost: ProfileAnalysisAiCostSchema.optional(),
      })
      .strict()
      .optional(),
    badges: z.array(ProfileAnalysisBadgeSchema).optional(),
    sentiment: nonEmptyString.optional(),
    sentimentOverview: nonEmptyString.optional(),
    insights: z.array(nonEmptyString).optional(),
    recommendations: z.array(nonEmptyString).optional(),
    activityPatterns: ProfileAnalysisActivityPatternsSchema.optional(),
    postingTimePatterns: ProfileAnalysisPostingTimePatternsSchema.optional(),
    surprisingFacts: z.array(nonEmptyString).optional(),
    listsSummary: nonEmptyString.optional(),
    range: RangeSchema.optional(),
  })
  .strict();

export type ProfileAnalysisSummarySection = z.infer<typeof ProfileAnalysisSummarySectionSchema>;
export type ProfileAnalysisTopic = z.infer<typeof ProfileAnalysisTopicSchema>;
export type ProfileAnalysisHighlight = z.infer<typeof ProfileAnalysisHighlightSchema>;
export type ProfileAnalysisPostingTimeBucket = z.infer<typeof ProfileAnalysisPostingTimeBucketSchema>;
export type ProfileAnalysisPostingTimePatterns = z.infer<typeof ProfileAnalysisPostingTimePatternsSchema>;
export type ProfileAnalysisStructure = z.infer<typeof ProfileAnalysisStructureSchema>;
